<template>
  <icon-button
    type="button"
    :icon="isEdit ? 'edit' : 'folder_special'"
    :classes="isEdit ? '-ico-only special' : 'save'"
    :label="isEdit ? 'Edit' : 'Save Collection'"
    :disabled="isDisabled"
    :icons-outlined="false"
    :reduce="isEdit"
    @click-action="openModal"
  />
</template>

<script setup lang="ts">
import IconButton from '@/components/buttons/icon-button.vue'
import { useModalsStore } from '@/stores/modals'
import { useSearchStore } from '@/stores/search'
import type { FilterCollection } from '@/types/model-types'
import { computed, watch } from 'vue'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    entityType: 'workout'
    filterCollection?: FilterCollection | null
    disabled?: boolean
  }>(),
  {
    filterCollection: null,
    disabled: false,
  },
)

/** EMITS **/
const emit = defineEmits(['saved'])

/** STATE **/
const modalsStore = useModalsStore()
const searchStore = useSearchStore()

/** COMPUTED **/
const isEdit = computed(() => !!props.filterCollection)
const modalId = computed(() =>
  props.filterCollection
    ? `filter-collection-form-${props.filterCollection.id}`
    : 'filter-collection-form',
)
const isDisabled = computed(() => {
  // deleting
  if (props.disabled) {
    return true
  }

  // edit
  if (isEdit.value) {
    return false
  }

  // add
  return !searchStore.hasAny
})

/** WATCHERS **/
watch(
  () => modalsStore.simpleModal.action,
  (newAction: string, prevAction: string) => {
    if (
      modalsStore.simpleModal.id === modalId.value &&
      newAction !== prevAction &&
      newAction === 'update'
    ) {
      // pass up the food chain and remove reactivity
      emit('saved', { ...modalsStore.simpleModal.actionPayload })
    }
  },
)

/** METHODS **/
function openModal() {
  modalsStore.openSimpleModal({
    type: 'FILTER_COLLECTION',
    id: modalId.value,
    props: {
      filterCollection: isEdit.value
        ? props.filterCollection
        : {
            type: props.entityType,
            queryParams: searchStore.apiParameters,
            privacy: 'private',
            canBeMadePublic: !searchStore.hasFilters({
              dropdown: Object.keys(searchStore.videoPrivateFilters()),
            }),
          },
    },
  })
}
</script>
